import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { AuDateInput } from "./AuDateInput";

const PHONE_LABELS = ["Mobile", "Work", "Home", "Fax", "Other"];

function PhoneList({ value, onChange, tid }) {
  const list = value || [];
  const upd = (i, k, v) => onChange(list.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <div className="space-y-2" data-testid={tid}>
      {list.map((p, i) => (
        <div key={i} className="flex gap-2">
          <select value={p.label || "Mobile"} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`${tid}-label-${i}`}
            className="h-9 w-28 rounded-md border border-input bg-background px-2 text-sm">
            {PHONE_LABELS.map((l) => <option key={l}>{l}</option>)}
          </select>
          <Input value={p.number || ""} placeholder="+61" onChange={(e) => upd(i, "number", e.target.value)} data-testid={`${tid}-number-${i}`} />
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`${tid}-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...list, { label: "Mobile", number: "" }])} data-testid={`${tid}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" />Add phone number
      </Button>
    </div>
  );
}

function PersonList({ value, onChange, tid }) {
  const list = value || [];
  const upd = (i, k, v) => onChange(list.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <div className="space-y-2" data-testid={tid}>
      {list.map((p, i) => (
        <div key={i} className="flex gap-2">
          <Input value={p.name || ""} placeholder="Contact name" onChange={(e) => upd(i, "name", e.target.value)} data-testid={`${tid}-name-${i}`} />
          <Input value={p.position || ""} placeholder="Position title" onChange={(e) => upd(i, "position", e.target.value)} data-testid={`${tid}-position-${i}`} />
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`${tid}-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...list, { name: "", position: "" }])} data-testid={`${tid}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" />Contact name + position title
      </Button>
    </div>
  );
}

function TagsArea({ value, onChange, tid }) {
  const [raw, setRaw] = useState((value || []).join("\n"));
  return <Textarea data-testid={tid} rows={6} placeholder={"One feature per line, e.g.\nCRM & Pipelines\nForms"} value={raw}
    onChange={(e) => { setRaw(e.target.value); onChange(e.target.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)); }} />;
}

export function FieldInput({ f, value, onChange }) {
  const tid = `field-${f.key}`;
  if (f.type === "tagsarea") return <TagsArea value={value} onChange={onChange} tid={tid} />;
  if (f.type === "readonly")
    return <div data-testid={tid} className="h-9 flex items-center rounded-md border border-dashed border-input bg-muted/40 px-3 text-sm text-muted-foreground">{value || "—"}</div>;
  if (f.type === "phonelist") return <PhoneList value={value} onChange={onChange} tid={tid} />;
  if (f.type === "personlist") return <PersonList value={value} onChange={onChange} tid={tid} />;
  if (f.type === "person")
    return <><Input data-testid={tid} list={`dl-${f.key}`} value={value ?? ""} placeholder="Pick or type a name" onChange={(e) => onChange(e.target.value)} /><datalist id={`dl-${f.key}`}>{(f.options || []).map((o) => <option key={o} value={o} />)}</datalist></>;
  if (f.type === "textarea")
    return <Textarea data-testid={tid} rows={f.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "select")
    return (
      <select data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
        <option value="">Select…</option>
        {(f.options || []).map((o) => {
          const [v, l] = typeof o === "string" ? [o, o] : [o.value, o.label];
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    );
  if (f.type === "checkbox")
    return <Switch data-testid={tid} checked={!!value} onCheckedChange={onChange} />;
  if (f.type === "info")
    return <div className="rounded-md border border-[#C2A462]/25 bg-[#C2A462]/5 p-3 text-xs text-slate-300" data-testid={tid}>{f.text}</div>;
  if (f.type === "multicheck")
    return (
      <div className="flex flex-wrap gap-2" data-testid={tid}>
        {f.options.map((o) => {
          const on = (value || []).includes(o.value);
          return <button type="button" key={o.value} data-testid={`${tid}-${o.value}`} onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...(value || []), o.value])}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-[#C2A462] bg-[#C2A462]/20 text-[#EBD6A0]" : "border-white/15 text-slate-400 hover:border-[#C2A462]/50"}`}>{on ? "✓ " : ""}{o.label}</button>;
        })}
      </div>
    );
  if (f.type === "tags")
    return <Input data-testid={tid} placeholder="comma,separated" value={(value || []).join(",")}
      onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />;
  if (f.type === "date")
    return <AuDateInput value={value} onChange={onChange} testid={tid} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" />;
  return <Input data-testid={tid} type={f.type || "text"} value={value ?? ""}
    onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
}

export function RecordDialog({ open, onOpenChange, title, fields, initial, onSave, drawer = false }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const close = (o) => { if (!o && drawer && JSON.stringify(form) !== JSON.stringify(initial || {}) && !window.confirm("You have unsaved changes. Close without saving?")) return; onOpenChange(o); };
  const submit = async () => {
    for (const f of fields) if (f.required && (!f.show || f.show(form)) && !String(form[f.key] ?? "").trim()) return toast.error(`${f.label} is required`);
    setBusy(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } catch {
      /* toast shown by caller */
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className={drawer ? "left-auto right-0 top-0 h-full max-h-screen w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-l border-[#C2A462]/20 sm:w-[600px] sm:max-w-[600px] data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:zoom-in-100" : "max-w-2xl max-h-[85vh] overflow-y-auto"} data-testid={drawer ? "record-drawer" : "record-dialog"}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle><DialogDescription className="sr-only">{title} form</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 py-1">
          {fields.filter((f) => !f.show || f.show(form)).map((f) => f.type === "heading" ? (
            <div key={f.key} className="sm:col-span-2 mt-2 border-b border-[#C2A462]/25 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#C2A462]" data-testid={`field-${f.key}`}>{f.label}</div>
          ) : (
            <div key={`${f.key}-${f.label}`} className={f.full || f.type === "textarea" || f.type === "phonelist" || f.type === "multicheck" ? "sm:col-span-2 space-y-1" : "space-y-1"}>
              <Label className="text-xs text-[#EBD6A0]/75">{f.label}{f.required && " *"}</Label>
              <FieldInput f={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />
            </div>
          ))}
        </div>
        <DialogFooter className={drawer ? "mt-6 border-t border-white/5 pt-4" : ""}>
          <Button variant="ghost" onClick={() => close(false)} data-testid="record-dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="record-dialog-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
